import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSIEMAlert, updateAlert } from '../../hooks/useSecOps';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
  info: 'bg-gray-100 text-gray-600',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  open: 'bg-red-100 text-red-700',
  investigating: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
  false_positive: 'bg-gray-100 text-gray-500',
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  new: ['open', 'investigating', 'false_positive'],
  open: ['investigating', 'resolved', 'false_positive'],
  investigating: ['resolved', 'closed', 'false_positive'],
  resolved: ['closed', 'open'],
  false_positive: ['open'],
  closed: ['open'],
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

export default function AlertDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useSIEMAlert(id ?? null);
  const [updating, setUpdating] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [comment, setComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  const alert = data as Record<string, unknown> | null;

  async function changeStatus(newStatus: string) {
    if (!id) return;
    setUpdating(true);
    try {
      await updateAlert(id, { status: newStatus });
      refresh();
    } catch { /* handled */ } finally {
      setUpdating(false);
    }
  }

  async function handleAssign() {
    if (!id || !assignTo.trim()) return;
    setUpdating(true);
    try {
      await updateAlert(id, { assigned_to: assignTo.trim() });
      setAssignTo('');
      refresh();
    } catch { /* handled */ } finally {
      setUpdating(false);
    }
  }

  async function handleAddComment() {
    if (!id || !comment.trim()) return;
    setAddingComment(true);
    try {
      await updateAlert(id, { add_comment: comment.trim() });
      setComment('');
      refresh();
    } catch { /* handled */ } finally {
      setAddingComment(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !alert) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">Failed to load alert</p>
          <p className="text-red-500 text-sm mt-1">{error ?? 'Alert not found'}</p>
          <button onClick={refresh} className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium">Retry</button>
        </div>
      </div>
    );
  }

  const status = (alert.status as string) ?? 'new';
  const transitions = STATUS_TRANSITIONS[status] ?? [];
  const techniques = (alert.mitre_techniques as string[]) ?? [];
  const comments = (alert.comments as Array<{ author: string; text: string; created_at: string }>) ?? [];
  const relatedIncidents = (alert.related_incidents as Array<{ id: string; title: string }>) ?? [];

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/secops/alerts')} className="text-sm text-gray-500 hover:text-gray-700">
          &larr; Back to Alerts
        </button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{alert.title as string}</h1>
          <div className="flex items-center gap-3 mt-2">
            <Badge value={alert.severity as string} colors={SEVERITY_COLORS} />
            <Badge value={status} colors={STATUS_COLORS} />
            <span className="text-sm text-gray-500">Source: {(alert.source as string) || 'Unknown'}</span>
            <span className="text-sm text-gray-500">Category: {(alert.category as string) || '-'}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Description</h2>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{(alert.description as string) || 'No description provided.'}</p>
          </div>

          {/* MITRE Techniques */}
          {techniques.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">MITRE ATT&CK Techniques</h2>
              <div className="flex flex-wrap gap-2">
                {techniques.map((t, i) => (
                  <span key={i} className="bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded font-mono">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Related Incidents */}
          {relatedIncidents.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Related Incidents</h2>
              <div className="space-y-2">
                {relatedIncidents.map((inc) => (
                  <button key={inc.id} onClick={() => navigate(`/secops/incidents/${inc.id}`)}
                    className="block text-sm text-indigo-600 hover:text-indigo-800 hover:underline">
                    {inc.title || inc.id}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Comments ({comments.length})</h2>
            {comments.length === 0 && <p className="text-sm text-gray-400">No comments yet.</p>}
            <div className="space-y-4 mb-4">
              {comments.map((c, i) => (
                <div key={i} className="border-l-2 border-gray-200 pl-4">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <span className="font-medium text-gray-700">{c.author}</span>
                    <span>{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                  </div>
                  <p className="text-sm text-gray-600">{c.text}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                onKeyDown={e => e.key === 'Enter' && handleAddComment()} />
              <button onClick={handleAddComment} disabled={addingComment || !comment.trim()}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {addingComment ? '...' : 'Add'}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Alert metadata */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Details</h2>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Detected</p>
              <p className="text-sm text-gray-700">{alert.detected_at ? new Date(alert.detected_at as string).toLocaleString() : '-'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Assigned To</p>
              <p className="text-sm text-gray-700">{(alert.assigned_to as string) || 'Unassigned'}</p>
              <div className="flex gap-1 mt-1">
                <input type="text" value={assignTo} onChange={e => setAssignTo(e.target.value)}
                  placeholder="Assign to..." className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs" />
                <button onClick={handleAssign} disabled={updating || !assignTo.trim()}
                  className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50">Assign</button>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Alert ID</p>
              <p className="text-xs text-gray-500 font-mono break-all">{alert.id as string}</p>
            </div>
          </div>

          {/* Status transitions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Update Status</h2>
            <div className="flex flex-wrap gap-2">
              {transitions.map(next => (
                <button key={next} onClick={() => changeStatus(next)} disabled={updating}
                  className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-40 capitalize">
                  {next.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Raw data */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Raw Event Data</h2>
            {alert.raw_event ? (
              <pre className="text-xs bg-gray-50 rounded p-3 overflow-auto max-h-64 text-gray-600">
                {JSON.stringify(alert.raw_event, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-gray-400">No raw event data available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
